import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Bus, MapPin, Clock, Phone, User, ShieldCheck, Navigation, AlertCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Transport = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('route'); // 'route' or 'vehicle'
    const [loading, setLoading] = useState(true);

    // Professional Dummy Data
    const transportData = {
        route: {
            routeName: "Route 04 - City South Express",
            busNo: "B-42",
            pickup: {
                point: "Lake View Apartments Main Gate",
                time: "07:15 AM",
                landmark: "Opposite HDFC ATM"
            },
            drop: {
                point: "Lake View Apartments Main Gate",
                time: "02:45 PM",
                landmark: "Opposite HDFC ATM"
            },
            stops: [
                { name: "Lake View Apartments", time: "07:15 AM", type: "Pickup" },
                { name: "Central Park Circle", time: "07:25 AM", type: "Stop" },
                { name: "High Street Crossing", time: "07:35 AM", type: "Stop" },
                { name: "Pragya Sarathi School", time: "07:50 AM", type: "Arrival" }
            ]
        },
        vehicle: {
            busPlate: "WB-01-AS-8842",
            model: "Tata Starbus 40-Seater",
            driver: {
                name: "Ramesh Kumar",
                phone: "+91 98765 43210",
                experience: "12 Years",
                photo: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=200&h=200&fit=crop"
            },
            attendant: {
                name: "Sita Devi",
                role: "Female Attendant",
                phone: "+91 98765 43211"
            },
            emergencyContact: "+91 33 2455 0000",
            status: "In Service",
            nextMaintenance: "2026-03-15"
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => setLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-pragyaBlue/20 border-t-pragyaBlue rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-32">
            {/* Header Section */}
            <div className="bg-white px-6 pt-6 pb-2 rounded-b-[2.5rem] shadow-sm border-b border-slate-100 sticky top-0 z-30">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-slate-50">
                        <ChevronLeft size={24} className="text-slate-600" />
                    </button>
                    <h2 className="text-lg font-bold text-slate-800">Transport Details</h2>
                    <div className="w-10" />
                </div>

                {/* Tabs */}
                <div className="flex p-1 bg-slate-100 rounded-2xl mb-4">
                    <button
                        onClick={() => setActiveTab('route')}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'route' ? 'bg-white text-pragyaBlue shadow-sm' : 'text-slate-500'}`}
                    >
                        Route & Timings
                    </button>
                    <button
                        onClick={() => setActiveTab('vehicle')}
                        className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'vehicle' ? 'bg-white text-pragyaBlue shadow-sm' : 'text-slate-500'}`}
                    >
                        Vehicle & Staff
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-5 mt-6">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, x: activeTab === 'route' ? -20 : 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: activeTab === 'route' ? 20 : -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                    >
                        {activeTab === 'route' ? (
                            <div className="space-y-4">
                                {/* Route Card */}
                                <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-12 h-12 bg-pragyaBlue/10 rounded-2xl flex items-center justify-center text-pragyaBlue">
                                            <Navigation size={24} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">{transportData.route.routeName}</h3>
                                            <p className="text-xs text-slate-400 font-bold">Bus Number: {transportData.route.busNo}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-green-50/50 p-4 rounded-2xl border border-green-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Clock size={14} className="text-green-600" />
                                                <span className="text-[10px] font-black text-green-700 uppercase">Pickup</span>
                                            </div>
                                            <p className="text-lg font-black text-slate-800 leading-none mb-1">{transportData.route.pickup.time}</p>
                                            <p className="text-[10px] text-slate-500 font-medium line-clamp-1">{transportData.route.pickup.point}</p>
                                        </div>
                                        <div className="bg-orange-50/50 p-4 rounded-2xl border border-orange-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Clock size={14} className="text-orange-600" />
                                                <span className="text-[10px] font-black text-orange-700 uppercase">Drop</span>
                                            </div>
                                            <p className="text-lg font-black text-slate-800 leading-none mb-1">{transportData.route.drop.time}</p>
                                            <p className="text-[10px] text-slate-500 font-medium line-clamp-1">{transportData.route.drop.point}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Stops Timeline */}
                                <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Route Timeline</h4>
                                    <div className="space-y-0">
                                        {transportData.route.stops.map((stop, index) => (
                                            <div key={index} className="flex gap-4 relative">
                                                {index !== transportData.route.stops.length - 1 && (
                                                    <div className="absolute left-[7px] top-4 bottom-0 w-[2px] bg-slate-100" />
                                                )}
                                                <div className={`w-4 h-4 rounded-full mt-1 z-10 border-2 border-white shadow-sm ${index === 0 ? 'bg-green-500' : index === transportData.route.stops.length - 1 ? 'bg-pragyaBlue' : 'bg-slate-300'}`} />
                                                <div className="pb-6 flex-1">
                                                    <div className="flex justify-between items-start">
                                                        <p className={`text-sm font-bold ${index === 0 || index === transportData.route.stops.length - 1 ? 'text-slate-800' : 'text-slate-500'}`}>{stop.name}</p>
                                                        <span className="text-xs font-black text-slate-400">{stop.time}</span>
                                                    </div>
                                                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-tighter">{stop.type}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Driver Card */}
                                <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                                    <div className="flex items-center gap-4 mb-6">
                                        <img src={transportData.vehicle.driver.photo} alt="Driver" className="w-16 h-16 rounded-2xl object-cover ring-2 ring-slate-50" />
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="text-md font-black text-slate-800">{transportData.vehicle.driver.name}</h3>
                                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-tight">Lead Driver • {transportData.vehicle.driver.experience} Exp</p>
                                                </div>
                                                <a href={`tel:${transportData.vehicle.driver.phone}`} className="p-3 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors">
                                                    <Phone size={20} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-50">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-300 uppercase leading-none mb-1">Attendant</p>
                                            <p className="text-sm font-bold text-slate-700">{transportData.vehicle.attendant.name}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-300 uppercase leading-none mb-1">Vehicle Plate</p>
                                            <p className="text-sm font-bold text-slate-700">{transportData.vehicle.busPlate}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Vehicle Info */}
                                <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-slate-50 rounded-lg text-slate-400">
                                            <ShieldCheck size={18} />
                                        </div>
                                        <h4 className="font-bold text-sm text-slate-800">Safety & Vehicle Info</h4>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center py-2 border-b border-slate-50">
                                            <span className="text-xs font-semibold text-slate-400">Status</span>
                                            <span className="text-xs font-black text-green-500 uppercase px-2 py-0.5 bg-green-50 rounded-md">{transportData.vehicle.status}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-slate-50">
                                            <span className="text-xs font-semibold text-slate-400">Model</span>
                                            <span className="text-xs font-bold text-slate-700">{transportData.vehicle.model}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2">
                                            <span className="text-xs font-semibold text-slate-400">Emergency Contact</span>
                                            <span className="text-xs font-bold text-pragyaBlue">{transportData.vehicle.emergencyContact}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Tracking Placeholder */}
                                <div className="bg-pragyaBlue/5 rounded-3xl p-6 border border-pragyaBlue/10 flex items-center gap-4">
                                    <div className="w-12 h-12 bg-pragyaBlue text-white rounded-2xl flex items-center justify-center shadow-lg shadow-pragyaBlue/20">
                                        <Navigation size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-extrabold text-pragyaBlue mb-0.5">Live Bus Tracking</p>
                                        <p className="text-[10px] text-pragyaBlue/60 font-bold uppercase tracking-widest">Coming Soon in v2.0</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Help/Notice */}
            <div className="px-5 mt-6">
                <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm flex items-start gap-4">
                    <div className="p-2 bg-orange-50 rounded-xl text-orange-500">
                        <AlertCircle size={18} />
                    </div>
                    <div>
                        <h4 className="font-bold text-sm text-slate-800">Delayed?</h4>
                        <p className="text-[11px] text-slate-400 font-medium leading-relaxed mt-1">
                            If the bus is more than 15 minutes late, please contact the lead driver directly or the school transport office.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Transport;
